import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { apiClient } from "@lib/api/apiClient";
import type { ApiResponse } from "@lib/api/api-types";
import type { Person } from "./OrganizationPage";

type Comparison = {
  legacyAssignmentId: number;
  userId: number;
  legacyManagerId: number;
  organizationManagerId: number | null;
  reconciliationStatus: string;
};
type History = {
  id: number;
  consultantName: string;
  managerName: string;
  effective_from: string;
  effective_to: string | null;
};
export function ReportingReconciliationPanel({
  people,
  onReview,
}: {
  people: Person[];
  onReview: (person: Person) => void;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const [page, setPage] = useState(1);
  const reconciliation = useQuery({
    queryKey: ["reporting-reconciliation"],
    queryFn: async () =>
      (
        await apiClient.get<ApiResponse<{ items: Comparison[] }>>(
          "/organization/reconciliation",
        )
      ).data.data,
  });
  const history = useQuery({
    queryKey: ["legacy-reporting-history", page],
    enabled: showHistory,
    queryFn: async () =>
      (
        await apiClient.get<
          ApiResponse<{ items: History[]; pagination: { totalPages: number } }>
        >("/sales/reporting/history", { params: { page, limit: 25 } })
      ).data.data,
  });
  const name = (id: number | null) =>
    people.find((person) => person.id === Number(id))?.name ||
    (id ? `User ${id}` : "No reporting manager");
  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Organization controls current lead visibility. Legacy sales assignments
        remain unchanged and provide historical evidence. Review conflicts
        individually; no relationships are copied automatically.
      </Alert>
      {reconciliation.isLoading && (
        <Typography>Loading assignment comparison…</Typography>
      )}
      {reconciliation.error && (
        <Alert severity="error">Unable to load assignment comparison.</Alert>
      )}
      <Table size="small" aria-label="Reporting reconciliation">
        <TableHead>
          <TableRow>
            {[
              "Employee",
              "Legacy manager",
              "Organization manager",
              "Status",
              "",
            ].map((label) => (
              <TableCell key={label}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {reconciliation.data?.items.map((row) => (
            <TableRow key={row.legacyAssignmentId}>
              <TableCell>{name(row.userId)}</TableCell>
              <TableCell>{name(row.legacyManagerId)}</TableCell>
              <TableCell>{name(row.organizationManagerId)}</TableCell>
              <TableCell>
                {row.reconciliationStatus.replaceAll("_", " ")}
              </TableCell>
              <TableCell>
                <Button
                  disabled={!people.some((p) => p.id === Number(row.userId))}
                  onClick={() => {
                    const person = people.find(
                      (p) => p.id === Number(row.userId),
                    );
                    if (person) onReview(person);
                  }}
                >
                  Review reporting
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {reconciliation.data?.items.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>No active legacy assignments.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Button onClick={() => setShowHistory(!showHistory)}>
        {showHistory
          ? "Hide legacy sales history"
          : "View legacy sales history"}
      </Button>
      {showHistory && (
        <>
          {history.error && (
            <Alert severity="error">Unable to load legacy history.</Alert>
          )}
          <Table size="small" aria-label="Legacy sales reporting history">
            <TableHead>
              <TableRow>
                {["Employee", "Manager", "From", "To"].map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {history.data?.items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.consultantName}</TableCell>
                  <TableCell>{row.managerName}</TableCell>
                  <TableCell>{row.effective_from}</TableCell>
                  <TableCell>
                    {row.effective_to || "Retained legacy assignment"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Stack direction="row">
            <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
              Previous history page
            </Button>
            <Button
              disabled={
                !history.data || page >= history.data.pagination.totalPages
              }
              onClick={() => setPage(page + 1)}
            >
              Next history page
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}
